import { useMemo, useState } from "react";
import { Eye } from "lucide-react";

import { DataTable } from "components/ui/DataTable/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { ROWS_OPTIONS, SORT_OPTIONS } from "features/labOrders/constants";
import { useLabOrderFacilities } from "features/labOrders/hooks/useLabOrderFacilities";
import { usePatientLabOrders } from "features/labOrders/hooks/usePatientLabOrders";
import { usePatientOutstandingLabTests } from "features/labOrders/hooks/usePatientOutstandingLabTests";
import {
  formatTestNames,
  resolveAgencyName,
  resolveOrderedByName,
  resolvePlacedByName,
} from "features/labOrders/utils/labOrderDisplay";
import {
  filterLabOrders,
  SEARCH_FETCH_ROWS,
} from "features/labOrders/utils/labOrderSearch";
import { summariseOrderOutstanding } from "features/labOrders/utils/outstandingLabTests";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { formatDate } from "utils/dateFormatter";

import { LabOrderStatusPill } from "../LabOrderStatusPill/LabOrderStatusPill";
import s from "./LabOrdersTable.module.css";

// Two laboratories at the same hospital are two different destinations, so the
// department is part of the identity of the row, not a detail.
const DestinationCell = ({ order }) => (
  <span className={s.destination}>
    <span className={s.hospital}>{order.hospitalName || "-"}</span>
    {!!order.agencyLabel && <span className={s.agency}>{order.agencyLabel}</span>}
  </span>
);

/*
 * The GP the order went out under, and — on the second line, when they are not the same
 * person — who placed it (KIN-2100). An order can be placed by a nurse under a GP's
 * Medical Council Number, and "Ordered by Dr Murphy" alone would say the wrong thing
 * about who was at the keyboard.
 */
const OrderedByCell = ({ order }) => {
  if (!order.orderedByName) return "-";

  return (
    <span className={s.destination}>
      <span className={s.hospital}>{order.orderedByName}</span>
      {!!order.placedByName && (
        <span className={s.agency}>Placed by {order.placedByName}</span>
      )}
    </span>
  );
};

// Truncated to the column; DataTableCell puts the full list on hover once it no longer
// fits. Nothing at all until the tests are known — a dash would read as "no tests",
// which a completed order never has.
const TestsCell = ({ order }) => {
  const tests = formatTestNames(order);
  if (!tests) return null;

  return (
    <span className={s.tests} data-testid="lab-order-tests">
      {tests}
    </span>
  );
};

/*
 * What this order is still waiting on (KIN-2087): "2 of 4 back, 1 closed. Vitamin D
 * outstanding, overdue". The count is the order's own testProgress (KIN-2177); nothing is
 * drawn for an order without one, because its tests are not tracked and "All N back"
 * would tell a GP results are in when they are not.
 */
const OutstandingCell = ({ order, outstandingTests, isOutstandingLoaded }) => {
  const summary = summariseOrderOutstanding({
    testProgress: order.testProgress,
    outstandingTests,
    isOutstandingLoaded,
  });

  if (!summary.summary) return null;

  return (
    <span className={s.outstanding}>
      <span className={s.outstandingText} title={summary.summary}>
        {summary.summary}
      </span>
      {summary.overdueCount > 0 && (
        <Pill {...PILL_COLORS.statusError}>Overdue</Pill>
      )}
    </span>
  );
};

/*
 * Every text column is a readable floor and a ceiling, and the table is sized to its card
 * rather than to its content (`noScroll` below) — the same shape the vaccines table was
 * given for the same fault (KIN-1894).
 *
 * The floor used to be `min-content`, and these cells are nowrap, so their min-content is
 * the whole untruncated string: one order of three tests dragged the grid wider than the
 * card and put a horizontal scrollbar under the row. A ceiling stops the laboratory and
 * the test list eating the row on a wide monitor; the floor is where they stop shrinking
 * on a narrow one, and between the two each cell truncates with its full text on hover.
 *
 * Status is the one column that takes the leftover width — the only `fr` of the five. It
 * is the shortest cell, but it is also the only one whose text is not worth truncating
 * ("Removed from work list"), and leaving the slack here keeps the laboratory and the
 * tests at a predictable width instead of reflowing them as statuses change.
 */
const getColumns = ({ outstandingByOrderId, isOutstandingLoaded }) => [
  {
    id: "created",
    header: "Date",
    width: "minmax(100px, 140px)",
    render: (row) => formatDate(row.created) || "-",
  },
  {
    id: "destination",
    header: "Hospital laboratory",
    width: "minmax(110px, 200px)",
    render: (row) => <DestinationCell order={row} />,
  },
  {
    id: "tests",
    header: "Tests",
    width: "minmax(140px, 370px)",
    render: (row) => <TestsCell order={row} />,
  },
  {
    id: "outstanding",
    header: "Outstanding",
    width: "minmax(130px, 240px)",
    wrap: true,
    render: (row) => (
      <OutstandingCell
        order={row}
        outstandingTests={outstandingByOrderId.get(String(row.id)) ?? []}
        isOutstandingLoaded={isOutstandingLoaded}
      />
    ),
  },
  {
    id: "orderedBy",
    header: "Ordered by",
    width: "minmax(90px, 160px)",
    render: (row) => <OrderedByCell order={row} />,
  },
  {
    id: "status",
    header: "Status",
    // `wrap` stops the cell clipping a pill that is taller than the row's 20px line box.
    width: "minmax(100px, 1fr)",
    wrap: true,
    render: (row) => <LabOrderStatusPill order={row} />,
  },
];

// `isBusy` folds an outside wait — confirming a return from Healthlink — into the
// table's own loading row, so the section never swaps its content for a spinner.
export const LabOrdersTable = ({ patientId, onSelectOrder, isBusy = false }) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({ field: "newest-first", direction: "" });
  const [search, setSearch] = useState("");

  const isSearching = !!search.trim();

  // The backend paginates and has no search parameter, so a search fetches one large
  // page and filters it here rather than searching only what happens to be on screen —
  // otherwise "no results" would mean "not on this page", which is a lie.
  const { data, isLoading } = usePatientLabOrders({
    patientId,
    page: isSearching ? 1 : page,
    rows: isSearching ? SEARCH_FETCH_ROWS : rowsPerPage,
    sorting: sort.field,
  });

  // An order carries the department's code and the clinician's id only; both are
  // resolved to names from data the practice already has cached.
  const { data: facilities } = useLabOrderFacilities();
  const { data: staffMembers } = useAllStaffMembers();

  // One call for the patient, grouped by order, rather than one per row.
  // `isSuccess`, not `data ?? []`: a list still loading, or one that failed, must not be
  // read as "nothing waiting" (KIN-2177).
  const { data: outstandingTests, isSuccess: isOutstandingLoaded } =
    usePatientOutstandingLabTests(patientId);
  const outstandingByOrderId = useMemo(() => {
    const grouped = new Map();

    (outstandingTests ?? []).forEach((test) => {
      const key = String(test.labOrderId);
      grouped.set(key, [...(grouped.get(key) ?? []), test]);
    });

    return grouped;
  }, [outstandingTests]);

  const columns = useMemo(
    () => getColumns({ outstandingByOrderId, isOutstandingLoaded }),
    [outstandingByOrderId, isOutstandingLoaded]
  );

  // Named before filtering, because the search matches on the resolved laboratory and
  // clinician names a GP can actually see — not on the codes the message carried.
  const named = (data?.data ?? []).map((order) => ({
    ...order,
    agencyLabel: resolveAgencyName(order, facilities),
    orderedByName: resolveOrderedByName(order, staffMembers),
    placedByName: resolvePlacedByName(order, staffMembers),
  }));
  const rows = isSearching ? filterLabOrders(named, search) : named;

  // A patient with more orders than one fetch covers would have matches outside the
  // window; say so rather than let the GP assume there are none.
  const isSearchWindowExceeded =
    isSearching && (data?.totalRecords ?? 0) > SEARCH_FETCH_ROWS;

  // The details view arrives with the order-outcome work; until a handler is
  // passed there is nothing to open, so no action column is offered.
  const rowActions = onSelectOrder
    ? [
        {
          label: "Open",
          icon: Eye,
          primary: true,
          onClick: (row) => onSelectOrder(row),
        },
      ]
    : undefined;

  return (
    <div data-testid="lab-orders-table">
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        isLoading={isLoading || isBusy}
        emptyTitle={isSearching ? "No matching lab orders" : "No lab orders yet"}
        emptyText={
          isSearching
            ? isSearchWindowExceeded
              ? `No match in this patient's ${SEARCH_FETCH_ROWS} most recent orders. Clear the search to page through the rest.`
              : "No order matches your search."
            : "No laboratory orders have been placed for this patient."
        }
        search={{
          value: search,
          onChange: (value) => {
            setSearch(value);
            setPage(1);
          },
          placeholder: "Search orders",
        }}
        sort={sort}
        sortOptions={SORT_OPTIONS}
        onSortChange={(field, direction) => {
          setSort({ field, direction });
          setPage(1);
        }}
        rowActions={rowActions}
        actionsColumnWidth="80px"
        noScroll
        currentPage={isSearching ? 1 : page}
        rowsPerPage={isSearching ? rows.length || 1 : rowsPerPage}
        rowsValue={rowsPerPage}
        rowsOptions={ROWS_OPTIONS}
        totalCount={isSearching ? rows.length : data?.totalRecords}
        onPageChange={setPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
      />
    </div>
  );
};
